import React, { useEffect, useState, useRef } from 'react';
import { useKeycloakToken } from '../common/useKeycloak';
import { useNavigate } from 'react-router-dom';
import {
  ProviderType,
  buildProviderType,
  HIDE_TECHNOLOGY_CARD_SENTINEL,
} from '../common/ObjectTypes';
import { loadProviderTypes } from '../../api/routes/loadProviderTypesNewRoute';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../api/queryKeys';
import NewRouteStepOne from './NewRouteStepOne';
import { useKeycloak } from '@react-keycloak/web';
import GundiV1Modal from './GundiV1Modal';
import RouteProgressIndicator from './NewRouteProgressIndicator/RouteProgressIndicator';
import {
  trackRouteCreationStarted,
  trackRouteCreationAbandoned,
} from '../../analytics/analytics';
import {
  containerClasses,
  buttonClasses,
  wizardClasses,
} from '../common/tailwindClassnames';
import { getRouteKind } from '../common/routeKind';

const EMPTY_PROVIDER_TYPES: ProviderType[] = [];

export function ManageAddRouteStepOne() {
  type FilterTypes = {
    endpoint__in: FilterTags[];
    enabled: FilterTags[];
    type__in: FilterTags[];
    owner__in: FilterTags[];
    ordering: string;
    search: string;
  };
  function createFilterTypes(
    endpoint__in: FilterTags[],
    enabled: FilterTags[],
    type__in: FilterTags[],
    owner__in: FilterTags[],
    ordering: string,
    search: string,
  ): FilterTypes {
    return {
      endpoint__in: endpoint__in || [],
      enabled: enabled || [],
      type__in: type__in || [],
      owner__in: owner__in || [],
      ordering: ordering || '',
      search: search || '',
    };
  }
  type FilterTags = {
    name: string;
    id: string;
  };
  const { t } = useTranslation();
  const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
  const { keycloak, initialized } = useKeycloak();

  const [v1Modal, setV1Modal] = useState<boolean>(false);
  const [debouncedSearchStr, setDebouncedSearchStr] = useState('');
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  // A new search cancels the one in flight, so a slow answer for an older
  // search can never replace the newer one.
  const providerTypesQuery = useQuery({
    queryKey: queryKeys.newRouteProviderTypes(debouncedSearchStr),
    enabled: !!token && typeof token === 'string' && !keycloak.isTokenExpired(),
    queryFn: async ({ signal }) => {
      const [provider_types, error] = await loadProviderTypes(
        token as string,
        debouncedSearchStr,
        signal,
      );
      if (error) throw error;
      return provider_types.filter(
        (pt) => pt.description !== HIDE_TECHNOLOGY_CARD_SENTINEL,
      );
    },
  });
  const providerTypes: ProviderType[] =
    providerTypesQuery.data ?? EMPTY_PROVIDER_TYPES;

  const [selectedProviderType, setSelectedProviderType] =
    useState<ProviderType>(buildProviderType());
  const [isProviderTypeSelected, setIsProviderTypeSelected] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    trackRouteCreationStarted();
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, []);

  const handleSelectProviderType = (pt: ProviderType) => {
    if (pt.id === '') {
      setSelectedProviderType(pt);
      setV1Modal(true);
    } else {
      setIsProviderTypeSelected(true);
      setSelectedProviderType(pt);
    }
  };

  const handleCloseModal = () => {
    setV1Modal(false);
  };

  // Takes the value, not the event — the house convention for search handlers.
  const handleChangeProviderTypeSearchBar = (value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (value.length >= 3 || value.length === 0) {
      searchTimeout.current = setTimeout(() => {
        setDebouncedSearchStr(value);
      }, 500);
    }
  };

  const handleClickNext = () => {
    navigate(`/routes/new/configureprovider/${selectedProviderType.value}`);
  };

  const handleCancel = () => {
    trackRouteCreationAbandoned(1);
    navigate('/');
  };

  return (
    <div className={containerClasses.page}>
      {v1Modal && (
        <GundiV1Modal
          helpCenterUrl={selectedProviderType.help_center_url || ''}
          onToggleModal={handleCloseModal}
        />
      )}
      <div className={wizardClasses.band}>
        <div className={wizardClasses.row}>
          <h1 className={wizardClasses.title}>{t('New Route')}</h1>
        </div>
      </div>
      <div className={wizardClasses.body}>
        <div className={wizardClasses.row}>
          <RouteProgressIndicator
            stepZeroStatus="in-progress"
            stepOneStatus="upcoming"
            stepTwoStatus="upcoming"
            webhookStepStatus={
              getRouteKind(selectedProviderType) === 'webhook'
                ? 'upcoming'
                : undefined
            }
          />
          <NewRouteStepOne
            providerTypes={providerTypes}
            reloadProviderTypes={!providerTypesQuery.isFetched}
            loading={providerTypesQuery.isFetching}
            fetchError={providerTypesQuery.isError}
            onSelectProviderType={handleSelectProviderType}
            onSearchProviderType={handleChangeProviderTypeSearchBar}
          />
        </div>
      </div>

      <div className={wizardClasses.footer}>
        <div className="px-4 md:px-8 desktop:px-[60px]">
          <div
            className={`${wizardClasses.row} flex flex-row items-center gap-3`}
          >
            <button
              className={buttonClasses.primary}
              onClick={() => handleClickNext()}
              disabled={!isProviderTypeSelected}
              data-testid="ManageAddRouteStepOne-next-button"
            >
              {currentStep === 3 ? t('Finish') : t('Next')}
            </button>
            <button
              className={buttonClasses.cancel}
              type="button"
              onClick={handleCancel}
              data-testid="ManageAddRouteStepOne-cancel-button"
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
